import type {
  MentionAutocompleteOption,
  ReferenceAssetKind,
  ReferenceImage,
} from '@/types/conversation-input';
import {
  getReferenceAssetCompressionNotice,
  getReferenceAssetInsertViolation,
  type MentionResourceConstraints,
} from '@/lib/conversations/mention-resource-guards';

export function isReferenceAssetVideo(mimeType?: string): boolean {
  return mimeType?.startsWith('video/') ?? false;
}

export function isReferenceAssetAudio(mimeType?: string): boolean {
  return mimeType?.startsWith('audio/') ?? false;
}

export interface ReferenceAssetEntry {
  image: ReferenceImage;
  kind: ReferenceAssetKind;
  referenceImageIndex: number;
  referenceImageMimeType?: string;
  label: string;
}

export interface ReferenceAssetMentionDisplayEntry {
  imageId: string;
  kind: ReferenceAssetKind;
  referenceImageIndex: number;
  display: string;
}

function resolveReferenceAssetKind(image: ReferenceImage): ReferenceAssetKind {
  if (image.kind) return image.kind;
  if (isReferenceAssetVideo(image.mimeType)) return 'video';
  if (isReferenceAssetAudio(image.mimeType)) return 'audio';
  return 'image';
}

export function buildReferenceAssetLabel(params: {
  index: number;
  kind?: ReferenceAssetKind;
  mimeType?: string;
}): string {
  if (params.kind === 'video' || isReferenceAssetVideo(params.mimeType))
    return `视频${params.index}`;
  if (params.kind === 'audio' || isReferenceAssetAudio(params.mimeType))
    return `音频${params.index}`;
  return `图${params.index}`;
}

export function buildReferenceAssetMentionDisplay(index: number, kind: ReferenceAssetKind): string {
  if (kind === 'video') return `@参考视频[视频${index}]`;
  if (kind === 'audio') return `@参考音频[音频${index}]`;
  return `@参考图[图${index}]`;
}

export function buildReferenceAssetEntries(
  referenceImages: ReferenceImage[]
): ReferenceAssetEntry[] {
  let imageIndex = 0;
  let videoIndex = 0;
  let audioIndex = 0;

  return referenceImages.map((image) => {
    const kind = resolveReferenceAssetKind(image);
    const referenceImageIndex =
      kind === 'video' ? ++videoIndex : kind === 'audio' ? ++audioIndex : ++imageIndex;

    return {
      image,
      kind,
      referenceImageIndex,
      referenceImageMimeType: image.mimeType,
      label: buildReferenceAssetLabel({
        index: referenceImageIndex,
        kind,
        mimeType: image.mimeType,
      }),
    };
  });
}

export function buildReferenceAssetMentionDisplayEntries(
  referenceImages: ReferenceImage[]
): ReferenceAssetMentionDisplayEntry[] {
  return buildReferenceAssetEntries(referenceImages).map((entry) => ({
    imageId: entry.image.id,
    kind: entry.kind,
    referenceImageIndex: entry.referenceImageIndex,
    display: buildReferenceAssetMentionDisplay(entry.referenceImageIndex, entry.kind),
  }));
}

export function buildReferenceAssetAutocompleteOptions(params: {
  referenceImages: ReferenceImage[];
  keyword: string;
  allowedKinds: Array<'image' | 'video' | 'audio'>;
  mentionResourceConstraints?: MentionResourceConstraints;
  existingVideoDurationsByKey?: ReadonlyMap<string, number>;
  existingAudioDurationsByKey?: ReadonlyMap<string, number>;
}): MentionAutocompleteOption[] {
  const {
    referenceImages,
    keyword,
    allowedKinds,
    mentionResourceConstraints,
    existingVideoDurationsByKey,
    existingAudioDurationsByKey,
  } = params;

  return buildReferenceAssetEntries(referenceImages)
    .filter((entry) => {
      return allowedKinds.includes(entry.kind);
    })
    .map((entry) => {
      const violation = mentionResourceConstraints
        ? getReferenceAssetInsertViolation({
            image: entry.image,
            constraints: mentionResourceConstraints,
            existingDurationsByKey: existingVideoDurationsByKey ?? new Map(),
            existingAudioDurationsByKey,
          })
        : null;
      const compressionNotice = mentionResourceConstraints
        ? getReferenceAssetCompressionNotice(entry.image, mentionResourceConstraints)
        : null;
      return {
        kind: 'reference-image' as const,
        referenceImageId: entry.image.id,
        referenceImageUrl: entry.image.url,
        referenceImageFileSize: entry.image.fileSize,
        referenceImageIndex: entry.referenceImageIndex,
        referenceImageKind: entry.kind,
        referenceImageMimeType: entry.referenceImageMimeType,
        disabled: violation !== null,
        disabledReason: violation?.message,
        compressionNotice: compressionNotice ?? undefined,
      };
    })
    .filter((option) => {
      if (!keyword) return true;
      return buildReferenceAssetLabel({
        index: option.referenceImageIndex,
        kind: option.referenceImageKind,
        mimeType: option.referenceImageMimeType,
      })
        .toLowerCase()
        .includes(keyword);
    });
}
